import React, { useState } from "react";
import Navbar from "../Components/Header/Navbar";
import { Check, ChevronLeft, ChevronRight, UserPlus, CreditCard, Star, ShieldCheck, Zap, Loader2, Video, BarChart3, Bell, Globe, Trophy, MailCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { ACCOUNT_END_POINT } from "src/config";
import { transformSerialTimestamp } from "src/hooks/transformSerialTimestamp";
import { toast } from "react-hot-toast";
import { FaEyeSlash } from "react-icons/fa";
import { IoEye } from "react-icons/io5";

interface StepProps {
    currentStep: number;
    totalSteps: number;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const StepsData = (t: any) => [
    { id: 1, name: t("launch_parents.steps.advantages.label"), icon: Star, description: t("launch_parents.steps.advantages.description") },
    { id: 2, name: t("launch_parents.steps.profile.label"), icon: UserPlus, description: t("launch_parents.steps.profile.description") },
    { id: 3, name: t("launch_parents.steps.access.label"), icon: CreditCard, description: t("launch_parents.steps.access.description") },
];

const ProgressIndicator: React.FC<StepProps & { steps: any[] }> = ({ currentStep, steps }) => {
    return (
        <div className="mb-12">
            <div className="flex items-center justify-center">
                {steps.map((step, index) => (
                    <React.Fragment key={step.id}>
                        <div className="flex flex-col items-center relative z-10">
                            <div
                                className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 transform ${currentStep > step.id
                                    ? "bg-emerald-500 text-white shadow-[0_0_20px_rgba(16,185,129,0.4)]"
                                    : currentStep === step.id
                                        ? "bg-gradient-to-br from-purple-500 to-blue-600 text-white shadow-[0_0_25px_rgba(139,92,246,0.5)] scale-110"
                                        : "bg-gray-800 text-gray-500 border border-gray-700"
                                    }`}
                            >
                                {currentStep > step.id ? (
                                    <Check className="w-7 h-7" />
                                ) : (
                                    <step.icon className={`w-6 h-6 ${currentStep === step.id ? "animate-pulse" : ""}`} />
                                )}
                            </div>
                            <span
                                className={`mt-3 text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${currentStep >= step.id ? "text-white" : "text-gray-500"
                                    }`}
                            >
                                {step.name}
                            </span>
                        </div>

                        {index < steps.length - 1 && (
                            <div
                                className={`w-12 md:w-20 h-[2px] mx-[-4px] mb-6 transition-all duration-700 ${currentStep > step.id
                                    ? "bg-gradient-to-r from-emerald-500 to-purple-500"
                                    : "bg-gray-800"
                                    }`}
                            />
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

const AdvantagesStep: React.FC = () => {
    const { t } = useTranslation();

    const advantages = [
        {
            icon: Video,
            title: t("launch_parents.advantages.highlights.title"),
            description: t("launch_parents.advantages.highlights.description"),
            color: "from-purple-500 to-pink-500"
        },
        {
            icon: BarChart3,
            title: t("launch_parents.advantages.performance.title"),
            description: t("launch_parents.advantages.performance.description"),
            color: "from-blue-500 to-cyan-500"
        },
        {
            icon: Bell,
            title: t("launch_parents.advantages.notifications.title"),
            description: t("launch_parents.advantages.notifications.description"),
            color: "from-orange-500 to-yellow-500"
        },
        {
            icon: Globe,
            title: t("launch_parents.advantages.access.title"),
            description: t("launch_parents.advantages.access.description"),
            color: "from-green-500 to-emerald-500"
        },
        {
            icon: Trophy,
            title: t("launch_parents.advantages.radar.title"),
            description: t("launch_parents.advantages.radar.description"),
            color: "from-red-500 to-pink-500"
        },
        {
            icon: ShieldCheck,
            title: t("launch_parents.advantages.professional.title"),
            description: t("launch_parents.advantages.professional.description"),
            color: "from-indigo-500 to-purple-500"
        }
    ];

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-[10px] font-bold uppercase tracking-widest mb-4">
                    <Star className="w-3 h-3" /> {t("launch_parents.advantages.badge")}
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">
                    {t("launch_parents.advantages.title")}
                </h2>
                <p className="text-gray-400 max-w-2xl mx-auto">
                    {t("launch_parents.advantages.subtitle")}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {advantages.map((advantage, index) => (
                    <div
                        key={index}
                        className="group relative p-6 rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900/50 to-gray-800/30 hover:border-gray-700 transition-all duration-300 hover:scale-[1.02]"
                    >
                        <div className="relative z-10">
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${advantage.color} flex items-center justify-center mb-4`}>
                                <advantage.icon className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-lg font-bold text-white mb-2">
                                {advantage.title}
                            </h3>
                            <p className="text-sm text-gray-400">
                                {advantage.description}
                            </p>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-transparent group-hover:via-white/5 rounded-2xl transition-all duration-300" />
                    </div>
                ))}
            </div>

            <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-800/30">
                <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                            <Check className="w-5 h-5 text-white" />
                        </div>
                    </div>
                    <div>
                        <h4 className="font-bold text-white mb-1">
                            {t("launch_parents.advantages.conclusion.title")}
                        </h4>
                        <p className="text-sm text-gray-300">
                            {t("launch_parents.advantages.conclusion.description")}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AccountRegistration: React.FC<{
    formData: { name: string; email: string; phone: string; childName: string; password?: string; confirmPassword?: string };
    onChange: (field: string, value: string) => void;
}> = ({ formData, onChange }) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const isEmailValid = !formData.email || EMAIL_REGEX.test(formData.email.trim());
    return (
        <div className="space-y-6 max-w-md mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-white mb-2">{t("launch_parents.step3.title")}</h2>
                <p className="text-gray-400">{t("launch_parents.step3.subtitle")}</p>
            </div>
            <div className="space-y-5">
                {[
                    { label: t("launch_parents.step3.fields.name"), field: "name", type: "text", placeholder: t("launch_parents.step3.placeholders.name") },
                    { label: t("launch_parents.step3.fields.email"), field: "email", type: "email", placeholder: t("launch_parents.step3.placeholders.email") },
                    { label: t("launch_parents.step3.fields.password"), field: "password", type: "password", placeholder: t("launch_parents.step3.placeholders.password") },
                    { label: t("launch_parents.step3.fields.confirm_password"), field: "confirmPassword", type: "password", placeholder: t("launch_parents.step3.placeholders.confirm_password") },
                ].map((input) => (
                    <div key={input.field}>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 ml-1">
                            {input.label}
                        </label>
                        <div className="relative">
                            <input
                                type={
                                    input.field === "password"
                                        ? showPassword
                                            ? "text"
                                            : "password"
                                        : input.field === "confirmPassword"
                                            ? showConfirmPassword
                                                ? "text"
                                                : "password"
                                            : input.type
                                }
                                value={(formData as any)[input.field]}
                                onChange={(e) => onChange(input.field, e.target.value)}
                                className={`w-full px-5 py-4 pr-12 bg-gray-900/60 border rounded-2xl text-white placeholder-gray-600 focus:outline-none focus:ring-4 transition-all duration-300 ${input.field === "confirmPassword" && formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword
                                    ? "border-red-500/50 focus:border-red-500 focus:ring-red-500/10"
                                    : "border-gray-800 focus:border-purple-500 focus:ring-purple-500/10"
                                    }`}
                                placeholder={input.placeholder}
                                autoComplete={input.field === "password" || input.field === "confirmPassword" ? "new-password" : undefined}
                            />
                            {(input.field === "password" || input.field === "confirmPassword") && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        input.field === "password"
                                            ? setShowPassword((prev) => !prev)
                                            : setShowConfirmPassword((prev) => !prev)
                                    }
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                    tabIndex={-1}
                                    aria-label={
                                        (input.field === "password" ? showPassword : showConfirmPassword)
                                            ? t("launch_parents.step3.actions.hide_password")
                                            : t("launch_parents.step3.actions.show_password")
                                    }
                                >
                                    {input.field === "password" ? (
                                        showPassword ? (
                                            <IoEye className="w-5 h-5" />
                                        ) : (
                                            <FaEyeSlash className="w-5 h-5" />
                                        )
                                    ) : showConfirmPassword ? (
                                        <IoEye className="w-5 h-5" />
                                    ) : (
                                        <FaEyeSlash className="w-5 h-5" />
                                    )}
                                </button>
                            )}
                        </div>
                        {input.field === "email" && formData.email && !isEmailValid && (
                            <p className="mt-2 text-[10px] text-red-500 font-bold uppercase tracking-wider ml-1">
                                {t("launch_parents.step3.errors.email_invalid")}
                            </p>
                        )}
                        {input.field === "confirmPassword" && formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                            <p className="mt-2 text-[10px] text-red-500 font-bold uppercase tracking-wider ml-1">{t("launch_parents.step3.errors.password_mismatch")}</p>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const PaymentStep: React.FC = () => {
    const { t } = useTranslation();
    return (
        <div className="space-y-6 max-w-md mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-[10px] font-bold uppercase tracking-widest mb-4">
                    <Star className="w-3 h-3 fill-current" /> {t("launch_parents.step4.badge")}
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">{t("launch_parents.step4.title")}</h2>
                <p className="text-gray-400">{t("launch_parents.step4.subtitle")}</p>
            </div>

            <div className="relative group overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 border border-white/5 shadow-2xl">
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-purple-600/20 rounded-full blur-[80px] group-hover:bg-purple-600/30 transition-all duration-500" />

                <div className="relative z-10 flex flex-col items-center">
                    <div className="text-5xl font-black text-white mb-1 flex items-start tracking-tighter">
                        5,99<span className="text-2xl mt-1 ml-1 text-gray-400">€</span>
                    </div>
                    <div className="text-xs font-bold text-purple-400 uppercase tracking-widest mb-8">{t("launch_parents.step4.recurrence")}</div>

                    <div className="w-full space-y-4 mb-2">
                        {[
                            { text: t("launch_parents.step4.features.highlights"), icon: Zap },
                            { text: t("launch_parents.step4.features.radar"), icon: ShieldCheck },
                            { text: t("launch_parents.step4.features.physical"), icon: Check },
                            { text: t("launch_parents.step4.features.export"), icon: Check },
                        ].map((item, i) => (
                            <div key={i} className="flex items-center gap-3 text-sm font-medium text-gray-200">
                                <div className="p-1 rounded-lg bg-white/5">
                                    <item.icon className="w-4 h-4 text-emerald-400" />
                                </div>
                                {item.text}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const LaunchNew: React.FC = () => {
    const { t } = useTranslation();
    const steps = StepsData(t);
    const [currentStep, setCurrentStep] = useState(1);
    const [showEmailVerificationNotice, setShowEmailVerificationNotice] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        childName: "",
        password: "",
        confirmPassword: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState<string | null>(null);

    const handleFormChange = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (apiError) setApiError(null);
    };

    const registerAccount = async () => {
        setIsSubmitting(true);
        setApiError(null);

        const accessPos = Math.floor(Math.random() * 9) + 1;
        const timestampSec = Math.floor(Date.now() / 1000);
        const accessKey = transformSerialTimestamp(
            "WZ2308ES00007",
            accessPos,
            timestampSec
        );

        try {
            const bodyData = new FormData();
            bodyData.append("_method", "POST");
            bodyData.append("mail", formData.email);
            bodyData.append("password", formData.password || "");
            bodyData.append("name", formData.name.split(" ")[0]);
            bodyData.append("surname", formData.name.split(" ").slice(1).join(" ") || ".");
            bodyData.append("language_code", i18next.language === "es" ? "ES" : "EN");
            bodyData.append("country_code", "ES");
            bodyData.append("account_type_id", "2"); // Parent account
            bodyData.append("is_tutor", "1"); // Assuming parent registration from this page is tutor by default
            bodyData.append("access_key", accessKey);
            bodyData.append("access_pos", accessPos.toString());
            bodyData.append("access_sn", "WZ2308ES00007");

            const response = await fetch(`${ACCOUNT_END_POINT}`, {
                method: "POST",
                body: bodyData,
            });

            const responseData = await response.json();

            if (responseData.status === "error" && responseData.result) {
                setApiError(responseData.result.error_msg || t("launch_parents.toasts.error"));
                toast.error(responseData.result.error_msg || t("launch_parents.toasts.error"));
                return false;
            } else if (responseData.status === "ok") {
                toast.success(t("launch_parents.toasts.success"));
                return true;
            } else {
                throw new Error(responseData.message || t("launch_parents.toasts.error"));
            }
        } catch (error) {
            const msg = error instanceof Error ? error.message : t("launch_parents.toasts.error_unknown");
            setApiError(msg);
            toast.error(msg);
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    const createSubscription = async () => {
        window.location.href = "https://wizardfootball.com/login";
    };

    const canProceed = () => {
        switch (currentStep) {
            case 1: return true; // Always can proceed from advantages step
            case 2: return !!(
                formData.name &&
                formData.email &&
                EMAIL_REGEX.test(formData.email.trim()) &&
                formData.password &&
                formData.confirmPassword &&
                formData.password === formData.confirmPassword
            );
            case 3: return true; // Always can proceed to payment
            default: return false;
        }
    };

    const handleNext = async () => {
        if (currentStep === 2) {
            const success = await registerAccount();
            if (!success) return;
        }

        if (currentStep < 3 && canProceed()) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setShowEmailVerificationNotice(false);
            setCurrentStep(currentStep + 1);
        } else if (currentStep === 3) {
            setShowEmailVerificationNotice(true);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setShowEmailVerificationNotice(false);
            setCurrentStep(currentStep - 1);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1: return <AdvantagesStep />;
            case 2: return <AccountRegistration formData={formData} onChange={handleFormChange} />;
            case 3: return <PaymentStep />;
            default: return null;
        }
    };

    return (
        <div className="min-h-screen bg-black text-white selection:bg-purple-500/30">
            <Navbar />

            <div className="relative pt-32 pb-24 px-6 overflow-hidden">
                {/* Background Accents */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-purple-900/10 rounded-full blur-[120px] pointer-events-none" />
                <div className="absolute top-40 right-0 w-96 h-96 bg-blue-900/10 rounded-full blur-[100px] pointer-events-none" />

                <div className="max-w-6xl mx-auto relative z-10">
                    <div className="text-center mb-16 space-y-4">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md mb-2">
                            <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-300">{t("launch_parents.hero.badge")}</span>
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[0.9] bg-gradient-to-b from-white via-white to-white/40 bg-clip-text text-transparent italic">
                            WIZARD <br /> {t("for_parents")}
                        </h1>
                        <p className="text-lg md:text-xl text-gray-400 font-medium max-w-xl mx-auto leading-relaxed">
                            {t("launch_parents.hero.subtitle_start")} <span className="text-white">{t("launch_parents.hero.subtitle_end")}</span>
                        </p>
                    </div>

                    <ProgressIndicator currentStep={currentStep} totalSteps={3} steps={steps} />

                    <div className="bg-gray-900/40 backdrop-blur-2xl rounded-[40px] p-8 md:p-12 border border-white/5 shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent opacity-50" />
                        {renderStep()}

                        {currentStep === 3 && showEmailVerificationNotice && (
                            <div className="mt-8 rounded-3xl border border-cyan-500/20 bg-cyan-500/10 px-5 py-4 backdrop-blur-sm">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-2xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                                            <MailCheck className="w-5 h-5 text-cyan-300" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-white">
                                                {t("launch_parents.alerts.verify_email_title")}
                                            </p>
                                            <p className="text-xs text-cyan-100/80 mt-1 max-w-xl">
                                                {t("launch_parents.alerts.verify_email_description")}
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={createSubscription}
                                        className="inline-flex items-center justify-center rounded-2xl bg-cyan-400 px-4 py-2.5 text-xs font-black uppercase tracking-wide text-slate-950 transition-all duration-300 hover:bg-cyan-300 hover:scale-[1.02] shadow-lg shadow-cyan-500/20"
                                    >
                                        {t("launch_parents.alerts.verify_email_button")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row justify-between items-center gap-6 mt-12">
                        <button
                            onClick={handleBack}
                            disabled={currentStep === 1}
                            className={`group flex items-center gap-3 px-8 py-4 rounded-3xl font-bold transition-all duration-300 ${currentStep === 1
                                ? "opacity-0 pointer-events-none"
                                : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/5"
                                }`}
                        >
                            <ChevronLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                            {t("launch_parents.buttons.back")}
                        </button>

                        <button
                            onClick={handleNext}
                            disabled={!canProceed() || isSubmitting}
                            className={`group relative flex items-center gap-3 px-12 py-5 rounded-3xl font-black transition-all duration-500 transform hover:scale-[1.02] active:scale-95 ${canProceed() && !isSubmitting
                                ? "bg-white text-black shadow-[0_20px_40px_rgba(255,255,255,0.15)] hover:shadow-[0_25px_50px_rgba(255,255,255,0.25)]"
                                : "bg-gray-800 text-gray-500 cursor-not-allowed opacity-50"
                                }`}
                        >
                            <span className="relative z-10 flex items-center gap-2 uppercase tracking-wide">
                                {isSubmitting ? (
                                    <>
                                        {t("launch_parents.buttons.processing")}
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    </>
                                ) : (
                                    <>
                                        {currentStep === 3 ? t("launch_parents.buttons.activate") : t("launch_parents.buttons.continue")}
                                        <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                                    </>
                                )}
                            </span>
                        </button>
                    </div>

                    <div className="mt-16 text-center">
                        <p className="text-[10px] text-gray-600 uppercase font-bold tracking-[0.3em]">
                            {t("launch_parents.footer.powered_by")} &copy; 2026
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LaunchNew;
